import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { profileApi } from '../utils/api';
import type { UserProfile } from '../types';

interface ProfileForm {
  primary_naics: string;
  keywords: string;
  min_contract_value?: number;
  max_contract_value?: number;
  certifications: string;
}

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ProfileForm>();

  const loadProfile = useCallback(async () => {
    try {
      const response = await profileApi.getCurrentProfile();
      const profileData = response.data;
      setProfile(profileData);

      // Populate form fields
      setValue('primary_naics', profileData.primary_naics.join(', '));
      setValue('keywords', profileData.keywords.join(', '));
      setValue('min_contract_value', profileData.min_contract_value);
      setValue('max_contract_value', profileData.max_contract_value);
      setValue('certifications', profileData.certifications.join(', '));
    } catch (error: any) {
      toast.error('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  }, [setValue]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const onSubmit = async (data: ProfileForm) => {
    setIsUpdating(true);
    try {
      const updateData = {
        primary_naics: data.primary_naics.split(',').map((code) => code.trim()).filter(Boolean),
        keywords: data.keywords.split(',').map((keyword) => keyword.trim()).filter(Boolean),
        min_contract_value: data.min_contract_value,
        max_contract_value: data.max_contract_value,
        certifications: data.certifications.split(',').map((cert) => cert.trim()).filter(Boolean),
      };

      await profileApi.updateProfile(updateData);
      toast.success('Profile updated successfully');
      loadProfile();
    } catch (error: any) {
      toast.error('Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">No profile found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please complete the onboarding process first.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Update your profile to improve opportunity matching
          </p>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Company Description
                </label>
                <div className="mt-1 p-3 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-900">{profile.company_description}</p>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Contact support to update your company description
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Services & Products
                </label>
                <div className="mt-1 p-3 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-900">{profile.services_products}</p>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Contact support to update your services description
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Primary NAICS Codes *
                </label>
                <input
                  {...register('primary_naics', { required: 'Primary NAICS codes are required' })}
                  type="text"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="541511, 541512, 541519"
                />
                {errors.primary_naics && (
                  <p className="mt-1 text-sm text-red-600">{errors.primary_naics.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Comma-separated list of 6-digit NAICS codes
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Keywords *
                </label>
                <input
                  {...register('keywords', { required: 'Keywords are required' })}
                  type="text"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="software, consulting, development, cloud"
                />
                {errors.keywords && (
                  <p className="mt-1 text-sm text-red-600">{errors.keywords.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Comma-separated keywords for opportunity matching
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Min Contract Value ($)
                  </label>
                  <input
                    {...register('min_contract_value', { valueAsNumber: true })}
                    type="number"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max Contract Value ($)
                  </label>
                  <input
                    {...register('max_contract_value', { valueAsNumber: true })}
                    type="number"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Certifications
                </label>
                <input
                  {...register('certifications')}
                  type="text"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="8(a), WOSB, SDVOSB"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Comma-separated list of your certifications
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isUpdating ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Related NAICS Codes
            </h3>
            <div className="space-y-3">
              {profile.related_naics.slice(0, 8).map((item, index) => (
                <div key={index} className="bg-gray-50 p-3 rounded">
                  <span className="font-medium text-blue-600">{item.code}</span>
                  <span className="text-gray-600 ml-2">{item.explanation}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Profile Status
            </h3>
            <div className="flex items-center">
              <div
                className={`flex-shrink-0 w-3 h-3 rounded-full mr-3 ${
                  profile.profile_approved ? 'bg-green-400' : 'bg-yellow-400'
                }`}
              ></div>
              <span className="text-sm text-gray-700">
                {profile.profile_approved
                  ? 'Profile approved and active for matching'
                  : 'Profile pending approval'}
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Last updated: {new Date(profile.updated_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;